/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	OnInit,
	ChangeDetectionStrategy,
	ViewChild,
	Input,
	OnDestroy,
	Output,
	EventEmitter,
	forwardRef,
	OnChanges,
	SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as $ from 'jquery';
import '@appLegacy/vendor/spectrum.1.8.0';
import tinycolor from 'tinycolor2';
import { ColorUtils } from 'src/app/utils/color-utils';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => ColorPickerComponent),
	multi: true,
};

/**
 * Interface to control our usedColors. If this is provided, this causes Spectrum to draw a Checkmark over all matching cases in the palette
 * Example:
 *  {'#000000': 1}
 * Tells Spectrum to draw a checkmark indicating 'color used' over the #000000 color in the palette
 */
export interface IUsedColors {
	[key: string]: number;
}
/**
 * These are options which are passed into the Spectrum color picker
 */
export interface IColorPickerOptions {
	color?: tinycolor;
	flat?: boolean;
	showInput?: boolean;
	showInitial?: boolean;
	allowEmpty?: boolean;
	showAlpha?: boolean;
	disabled?: boolean;
	localStorageKey?: string;
	showPalette?: boolean;
	showPaletteOnly?: boolean;
	togglePaletteOnly?: boolean;
	showSelectionPalette?: boolean;
	clickoutFiresChange?: boolean;
	cancelText?: string;
	labelText?: string;
	chooseText?: string;
	clearText?: string;
	noColorSelectedText?: string;
	editLabelText?: string;
	addColorText?: string;
	togglePaletteMoreText?: string;
	togglePaletteLessText?: string;
	containerClassName?: string;
	replacerClassName?: string;
	preferredFormat?: string;
	maxSelectionSize?: number;
	palette?: [[string]];
	selectionPalette?: [string];
	noAutoClose?: boolean;
	hideAfterPaletteSelect?: boolean;
	usedColors?: IUsedColors;
	showAddColorButton?: boolean;
	onAddColor?: () => void;
}

const defaultColorPickerOptions: IColorPickerOptions = {
	showAlpha: false,
	showPalette: false,
	showSelectionPalette: false,
	showInput: true,
	preferredFormat: 'rgb',
};

@Component({
	selector: 'color-picker',
	templateUrl: './color-picker.component.html',
	styles: [],
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
	@ViewChild('spectrumColorPickerElement', { static: true }) public $element;

	@Input() public editLabelText = 'Select a color';
	@Input() public cancelText = 'Cancel';
	@Input() public labelText = '';
	@Input() public chooseText = 'Select';
	@Input() public noColorSelectedText = '';
	@Input() public addColorText = 'Add color';
	@Input() public alpha = 1;
	@Input() public format: string = defaultColorPickerOptions.preferredFormat;
	@Input() public options: IColorPickerOptions = defaultColorPickerOptions;
	@Output() public change: EventEmitter<string> = new EventEmitter();
	@Output() public move: EventEmitter<string> = new EventEmitter();
	@Output() public show: EventEmitter<string> = new EventEmitter();
	@Output() public hide: EventEmitter<string> = new EventEmitter();

	private readonly MAX_COLORS_PER_PALETTE = 120;

	public color: UntypedFormControl;
	public editColor: UntypedFormControl;

	protected _input: any;

	protected _valueChange: any;
	private started = false;

	private currentColor: string;
	private addColorCallback: (color: string) => void;

	constructor() {
		this.color = new UntypedFormControl('');
		this.editColor = new UntypedFormControl('');
	}

	public ngOnInit() {}

	public ngOnChanges(changes: SimpleChanges) {
		// force show/hide the color-picker with a button
		if (this._input) {
			// manually force spectrum to update the used colors selection
			this._input.spectrum('option', 'usedColors', changes.options.currentValue.usedColors);

			// force show/hide color picker
			if (changes.options.currentValue.forceShow !== changes.options.previousValue.forceShow) {
				this._input.spectrum('option', 'forceShow', changes.options.currentValue.forceShow);
				this._input.spectrum(changes.options.currentValue.forceShow ? 'show' : 'hide');
			}
		}
	}

	public showPicker() {
		// force show of the color-picker
		if (this._input) {
			this._input.spectrum('show');
		}
	}

	public ngOnDestroy() {
		// Calling .off() with no arguments removes all handlers attached to the elements
		this._input.off();
		this._input.spectrum('option', 'forceShow', false);
		this._input.spectrum('hide');
	}

	public writeValue(value: string) {
		const color = tinycolor(value);
		color.setAlpha(this.alpha);
		this.editColor.setValue(color);
		this.color.setValue(color);

		if (!this.started) {
			this.start();
			this.started = true;
		}
	}

	public registerOnChange(fn: any) {
		this._valueChange = fn;
	}

	public registerOnTouched(fn: any) {
		// TODO NYI
	}

	protected _onChange(e, tiny: tinycolor): void {
		if (!tiny) {
			return;
		}

		this.color.setValue(tiny);
		const stringified = tiny.toString(this.format);
		this._valueChange(stringified);
		this.currentColor = stringified;
		this.change.emit(stringified);
	}

	protected _onMove(e, tiny: tinycolor): void {
		if (!tiny) {
			return;
		}
		this.editColor.setValue(tiny);
		this.currentColor = tiny.toString();
		this.move.emit(this.currentColor);
	}

	protected _onShow(e, tiny: tinycolor): void {
		this.show.emit(this.color.value.toString());
	}

	protected _onHide(e, tiny: tinycolor): void {
		this.hide.emit(this.color.value.toString());
	}

	public start() {
		if (this.alpha < 1) {
			this.options['forceAlpha'] = true;
		}

		// insert this addColor()
		this.addColorCallback = this.options.onAddColor;
		this.options.onAddColor = () => this.addColor(this);

		this.options['color'] = this.color.value.toString(this.format);
		this.options['cancelText'] = this.cancelText;
		this.options['editLabelText'] = this.editLabelText;
		this.options['labelText'] = this.labelText;
		this.options['chooseText'] = this.chooseText;
		this.options['noColorSelectedText'] = this.noColorSelectedText;
		this.options['addColorText'] = this.addColorText;
		this._input = $('input', this.$element.nativeElement);

		this._input.spectrum(this.options);
		// this._input.spectrum(this.options, 'showInput', false);

		// Setup the jQuery -> Angular bindings
		this._input.on('change.spectrum', (e, tiny: tinycolor) => this._onChange(e, tiny));
		this._input.on('move.spectrum', (e, tiny: tinycolor) => this._onMove(e, tiny));
		this._input.on('show.spectrum', (e, tiny: tinycolor) => this._onShow(e, tiny));
		this._input.on('hide.spectrum', (e, tiny: tinycolor) => this._onHide(e, tiny));
	}

	public destroy() {
		this.ngOnDestroy();
	}

	public addColor(self: this): void {
		if (!self.currentColor || self.currentColor === '') {
			Logging.warning('Please pick a color.');
			return;
		}

		if (self.options.palette[0].length > self.MAX_COLORS_PER_PALETTE) {
			Logging.warning('Color limit reached.');
			return;
		}

		if (self.colorExists(self.currentColor)) {
			Logging.warning('Color already exists.');
			return;
		}

		if (self.addColorCallback) {
			self.addColorCallback(self.currentColor);
			this._input.spectrum('option', 'palette', this.options.palette);
		}
	}

	private colorExists(color: string): boolean {
		return this.options.palette[0].includes(color);
	}
}
